import { JourneyInteraction, JourneyKpis, JourneyTransition } from '../../../models/journey-explorer/journey-explorer';
import { formatCompact, formatDuration, formatNumber } from './journey-explorer-utils';

export type ExplainTone = 'good' | 'bad' | 'neutral';

export interface ExplainSection {
	title: string;
	value: string;
	text: string[];
	tone?: ExplainTone;
}

export interface ExplainContext {
	kpis: JourneyKpis;
	goalLabel: string | null;
	label: (name: string) => string;
}

const LONG_GAP_SECS = 3_600;
const QUICK_GAP_SECS = 120;
const SKEW_RATIO = 10;
const NOTABLE_POINTS = 10;
const NEIGHBOUR_STEPS = 3;

const pct = (part: number, whole: number): number => (whole > 0 ? (part / whole) * 100 : 0);
const pctText = (value: number): string => `${Math.round(value)}%`;
const gapText = (secs: number | null): string => (secs !== null && secs < 1 ? 'under a second' : formatDuration(secs));
const top = (transitions: JourneyTransition[]): JourneyTransition[] =>
	[...transitions].sort((a, b) => b.customers - a.customers).slice(0, NEIGHBOUR_STEPS);

const gapReading = (median: number | null, avg: number | null): string | null => {
	const typical = median ?? avg;
	if (typical === null) {
		return null;
	}
	if (median !== null && avg !== null && avg >= LONG_GAP_SECS && avg > median * SKEW_RATIO) {
		return (
			`Most customers move on in about ${formatDuration(median)}, but some leave and come back hours or ` +
			`days later. Those long gaps pull the average up to ${formatDuration(avg)}, so this is where people ` +
			'drop out of the visit and return later, not a slow step.'
		);
	}
	if (typical <= QUICK_GAP_SECS) {
		return 'Customers move on quickly here. This is usually done in one sitting, while they are engaged.';
	}
	return 'Gaps are fairly similar from customer to customer, so the median and the average tell the same story.';
};

// The median leads; the average is shown second because a few very long gaps can distort it.
const timeSection = (
	title: string,
	median: number | null,
	avg: number | null,
	lead: (gap: string, kind: string) => string
): ExplainSection => {
	const typical = median ?? avg;
	return {
		title,
		value: formatDuration(typical),
		text: [
			typical === null
				? 'No customer had a next step inside the interaction dates, so there is no time to show.'
				: lead(gapText(typical), median !== null ? 'typical (median)' : 'average'),
			...(median !== null && avg !== null ? [`The average is ${gapText(avg)}.`] : []),
			...[gapReading(median, avg)].filter((line): line is string => line !== null),
		],
	};
};

const comparison = (rate: number, baseline: number, baselineText: string): { text: string; tone: ExplainTone } => {
	const diff = rate - baseline;
	if (diff >= NOTABLE_POINTS) {
		return {
			text: `That's well above the ${pctText(baseline)} for ${baselineText}, a strong sign someone will convert.`,
			tone: 'good',
		};
	}
	if (diff <= -NOTABLE_POINTS) {
		return {
			text: `That's well below the ${pctText(
				baseline
			)} for ${baselineText}, so customers here are less likely to convert.`,
			tone: 'bad',
		};
	}
	return { text: `That's close to the ${pctText(baseline)} for ${baselineText}.`, tone: 'neutral' };
};

export const explainInteraction = (
	stat: JourneyInteraction,
	incoming: JourneyTransition[],
	outgoing: JourneyTransition[],
	{ kpis, goalLabel, label }: ExplainContext
): ExplainSection[] => {
	const name = label(stat.name);
	const sections: ExplainSection[] = [
		{
			title: 'Customers',
			value: formatCompact(stat.customers),
			text: [
				`${formatNumber(stat.customers)} customers did ${name} at least once, ` +
					`${pctText(pct(stat.customers, kpis.customers))} of the ${formatNumber(kpis.customers)} in view.`,
				`It happened ${formatNumber(stat.interactions)} times in total, about ` +
					`${(stat.customers > 0 ? stat.interactions / stat.customers : 0).toFixed(1)} times per customer.`,
			],
		},
		timeSection(
			'Median time to the next step',
			stat.median_secs_to_next,
			stat.avg_secs_to_next,
			(gap, kind) => `The ${kind} gap between ${name} and whatever the customer does next is ${gap}.`
		),
	];

	if (goalLabel) {
		const { text, tone } = comparison(stat.reached_pct, kpis.goal_rate_pct, 'all customers in view');
		sections.push({
			title: 'Goal rate',
			value: pctText(stat.reached_pct),
			tone,
			text: [
				`${pctText(stat.reached_pct)} of customers who did ${name} (${formatNumber(stat.reached)} of ` +
					`${formatNumber(stat.customers)}) went on to reach ${goalLabel} afterwards.`,
				text,
			],
		});
	}

	sections.push({
		title: 'Ended here',
		value: formatCompact(stat.exits),
		text: [
			`${formatNumber(stat.exits)} customers (${pctText(pct(stat.exits, stat.customers))}) had nothing after ` +
				`${name} inside the interaction dates. It was the last thing they did.`,
		],
	});

	const neighbours = (transitions: JourneyTransition[], side: 'from' | 'to', timing: (gap: string) => string) =>
		top(transitions).map(
			(transition) =>
				`${label(transition[side])}: ${formatCompact(transition.customers)} customers ` +
				`(${pctText(pct(transition.customers, stat.customers))}), ` +
				timing(gapText(transition.median_secs ?? transition.avg_secs))
		);
	const prior = neighbours(incoming, 'from', (gap) => `typically ${gap} before`);
	if (prior.length > 0) {
		sections.push({ title: 'Most common prior steps', value: '', text: prior });
	}
	const next = neighbours(outgoing, 'to', (gap) => `typically ${gap} after`);
	if (next.length > 0) {
		sections.push({ title: 'Most common next steps', value: '', text: next });
	}
	return sections;
};

export const explainTransition = (
	stat: JourneyTransition,
	from: JourneyInteraction | undefined,
	{ goalLabel, label }: ExplainContext
): ExplainSection[] => {
	const source = label(stat.from);
	const target = label(stat.to);
	const share = from
		? ` That's ${pctText(pct(stat.customers, from.customers))} of the ${formatNumber(
				from.customers
		  )} who did ${source}.`
		: '';
	const sections: ExplainSection[] = [
		{
			title: 'Customers',
			value: formatCompact(stat.customers),
			text: [
				`${formatNumber(stat.customers)} customers went from ${source} straight to ${target}.${share}`,
				`It happened ${formatNumber(stat.transitions)} times in total.`,
			],
		},
		timeSection(
			'Median time between the steps',
			stat.median_secs,
			stat.avg_secs,
			(gap, kind) =>
				`This only counts the times ${target} came straight after ${source}. In those cases the ${kind} ` +
				`gap is ${gap}.`
		),
	];

	if (goalLabel) {
		const lines = [
			`${pctText(stat.reached_pct)} of customers who took this exact step (${formatNumber(stat.reached)} of ` +
				`${formatNumber(stat.customers)}) reached ${goalLabel}.`,
		];
		let tone: ExplainTone = 'neutral';
		if (from) {
			const result = comparison(stat.reached_pct, from.reached_pct, `everyone who did ${source}`);
			lines.push(result.text);
			tone = result.tone;
		}
		sections.push({ title: 'Goal rate', value: pctText(stat.reached_pct), tone, text: lines });
	}
	return sections;
};
